
import React, { useEffect, useRef, useState } from 'react'
import { db, storage } from '../../firebase'
import { getDownloadURL, uploadBytes, ref as sRef } from 'firebase/storage'
import { Link, useNavigate } from 'react-router-dom'
import Papa from 'papaparse';
import { getPlatforms, createPlatform, updatePlatform, deletePlatform } from '../../api/platforms'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert } from '@mui/material'

const AdminPlatforms = () => {

    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const successBtn = useRef()

    const fileInputRef = useRef(null);
    const [imgPreview, setImgPreview] = useState(false)
    const [imgSrc, setImgSrc] = useState('')
    const [uploadLabel, setUploadLabel] = useState('Choose file')
    const [previewId, setPreviewId] = useState('')

    const queryClient = useQueryClient()

    const { 
        data: platforms, 
        isLoading, 
        error: fetchError 
    } = useQuery({
        queryKey: ['platforms'],
        queryFn: getPlatforms,
        select: (data) => data.platforms
    })

    const uploadArtwork = async (e) => {
        const file = e.target.files[0]
        const storageRef = sRef(storage, `platforms/${file.name}`);
      
        try {
          // Upload file to Firebase Storage
          await uploadBytes(storageRef, file);
      
          // Get download URL
          const downloadURL = await getDownloadURL(storageRef);
          setImgSrc(downloadURL)
          setImgPreview(true)
          setUploadLabel('Replace file')
        } catch (error) {
          console.error("Error uploading file:", error);
        }
    }      

    const removeImg = () => {
        fileInputRef.current.value = '';
        setImgSrc('')
        setImgPreview(false)
        setUploadLabel('Choose file')
    }

    const createMutation = useMutation({
        mutationFn: createPlatform,
        onSuccess: (newPlatform) => {
            queryClient.invalidateQueries(['platforms'])
            successBtn.current.click()
            // Reset form
            setTitle('')
            setDescription('')
            setImgPreview(false)
            setUploadLabel('Choose file')
        },
        onError: (error) => {
            setError(error.message || 'Failed to create platform')
        }
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updatePlatform(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['platforms'])
            successBtn.current.click()
        },
        onError: (error) => {
            setError(error.message || 'Failed to update platform')
        }
    })

    const deleteMutation = useMutation({
        mutationFn: deletePlatform,
        onSuccess: () => {
            queryClient.invalidateQueries(['platforms'])
        },
        onError: (error) => {
            setError(error.message || 'Failed to delete platform')
        }
    })

    const handleAddPlatform = async (e) => {
        e.preventDefault()
        createMutation.mutate({
            title,
            description,
            logo: imgSrc
        })
    }

    const handleUpdatePlatform = async (platformId) => {
        updateMutation.mutate({
            id: platformId,
            data: {
                title,
                description,
                logo: imgSrc
            }
        })
    }

    const handleDeletePlatform = async (platformId) => {
        deleteMutation.mutate(platformId)
    }

    useEffect(() => {
        if (error) {
            console.error(error)
            setTimeout(() => setError(null), 5000)
        }
    }, [error])

    return (
        <div className="admin-page py-5">
            <div className='container'>
                <h1 className='dash-title'>Platforms</h1>
                <div className="row">
                    <div className="col-12 px-1 mt-4">
                        <div className="dash-card">
                            <div className="dash-card-heading">
                                <h4>Platforms</h4>
                                <button 
                                    className="dash-card-heading-btn"
                                    data-bs-toggle="modal"
                                    data-bs-target="#addPlatformModal"
                                    onClick={() => {
                                        setTitle('')
                                        setDescription('')
                                        setImgSrc('')
                                    }}
                                >
                                    <i className="fi fi-sr-plus"></i>
                                    Add Platform
                                </button>
                            </div>
                            <div className="table-responsive mt-4">
                                {/* Loading State */}
                                {isLoading && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
                                        <CircularProgress />
                                    </div>
                                )}

                                {/* Error State */}
                                {fetchError && (
                                    <Alert 
                                        severity="error" 
                                        sx={{ margin: '20px 0' }}
                                    >
                                        Error loading platforms: {fetchError.message}
                                    </Alert>
                                )}

                                {/* Data Table */}
                                {!isLoading && !fetchError && platforms && (
                                    <table className="table dash-table">
                                        <thead>
                                            <tr>
                                                <td>Platform</td>
                                                <td>Description</td>
                                                <td>Action</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {platforms.map((platform, i) => (
                                                <tr key={i}>
                                                    <td>
                                                        <div className="table-item d-flex align-items-center">
                                                            <img className='platform-img' src={platform.logo} alt="" />
                                                            <h6 className='ms-2'>{platform.title}</h6>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className='table-item'>{platform.description}</div>
                                                    </td>
                                                    <td>
                                                        <div className='table-item d-flex'>
                                                            <i 
                                                                className="fi fi-rr-pencil table-action-btn a-blue me-2"
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#editPlatformModal"
                                                                onClick={() => {
                                                                    setTitle(platform.title)
                                                                    setDescription(platform.description)
                                                                    setImgPreview(true)
                                                                    setImgSrc(platform.logo)
                                                                    setPreviewId(platform.platform_id)
                                                                }}
                                                            ></i>
                                                            <i 
                                                                className="fi fi-rr-trash table-action-btn a-red"
                                                                onClick={() => handleDeletePlatform(platform.platform_id)}
                                                            ></i>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add error display */}
            {error && (
                <Alert 
                    severity="error" 
                    sx={{ margin: '20px 0' }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Show loading state for mutations */}
            {(createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading) && (
                <div style={{ 
                    position: 'fixed', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    zIndex: 9999 
                }}>
                    <CircularProgress />
                </div>
            )}

            <div className="modal fade" id="addPlatformModal" tabindex="-1" aria-labelledby="addPlatformModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id="addPlatformModalLabel">Add Platform</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Logo</label>
                                        <div className="img-file-upload">
                                            {
                                                imgPreview && (
                                                    <div className='img-preview logo-preview'>
                                                        <img src={imgSrc} alt="" />
                                                        <i className="fi fi-rr-trash remove-artwork" onClick={removeImg}></i>
                                                    </div>
                                                )
                                            }
                                            <div className="dash-form-file logo-uploader">
                                                <i className="fi fi-rr-cloud-upload"></i>
                                                {uploadLabel}
                                                <input type="file" ref={fileInputRef} onChange={uploadArtwork} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Title</label>
                                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder='Title' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Description</label>
                                        <textarea className='dash-form-textarea' value={description} onChange={e => setDescription(e.target.value)}></textarea>
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-btns">
                                        <button 
                                            className='dash-form-btn' 
                                            onClick={handleAddPlatform}
                                        >Submit</button>
                                    </div>
                                </div>
                                <button data-bs-toggle="modal" data-bs-target="#successModal" ref={successBtn} hidden></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="editPlatformModal" tabindex="-1" aria-labelledby="editPlatformModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5" id="editPlatformModalLabel">Edit Platform</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Logo</label>
                                        <div className="img-file-upload">
                                            {
                                                imgPreview && (
                                                    <div className='img-preview logo-preview'>
                                                        <img src={imgSrc} alt="" />
                                                        <i className="fi fi-rr-trash remove-artwork" onClick={removeImg}></i>
                                                    </div>
                                                )
                                            }
                                            <div className="dash-form-file logo-uploader">
                                                <i className="fi fi-rr-cloud-upload"></i>
                                                {uploadLabel}
                                                <input type="file" ref={fileInputRef} onChange={uploadArtwork} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Title</label>
                                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder='Title' className='dash-form-input' />
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-group">
                                        <label>Description</label>
                                        <textarea className='dash-form-textarea' value={description} onChange={e => setDescription(e.target.value)}></textarea>
                                    </div>
                                </div>
                                <div className="col-12 mt-3">
                                    <div className="dash-form-btns">
                                        <button 
                                            className='dash-form-btn' 
                                            onClick={() => handleUpdatePlatform(previewId)}
                                        >Submit</button>
                                    </div>
                                </div>
                                <button data-bs-toggle="modal" data-bs-target="#successModal" ref={successBtn} hidden></button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body success-modal">
                            <a className='success-close-btn' href="/admin/platforms">
                                <i className="fi fi-rr-cross"></i>
                            </a>
                            <i className="fi fi-rr-check-circle text-success success-icon"></i>
                            <h5>Platforms Updated Successfully!</h5>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AdminPlatforms